import React, { useMemo } from 'react';
import { Anchor, Box, Hint, Text } from '@common-fe/common-fe';

import Permissions from '@/common/permissions';
import { Access } from '@/modules/core/components';
import { useCurrentOrganization } from '@/modules/core/hooks';
import { EmployerStatus } from '@/modules/employer/employer.constants';

import { TEMPLATE_UNDER_UPDATE_HINT } from '../../PlanSetup.constants';
import { useEditModeStore, usePreviewStore } from '../../stores';

interface Props {
  isTemplateMode?: boolean;
  isPlanActive?: boolean;
  isPlanTerminated?: boolean;
  isTemplateUnderUpdate?: boolean;
}

const SwitchToEditMode: React.FC<Props> = ({
  isTemplateMode,
  isPlanActive,
  isPlanTerminated,
  isTemplateUnderUpdate,
}) => {
  const previewMode = usePreviewStore((state) => state.previewMode);
  const setEditMode = useEditModeStore((state) => state.setEditMode);

  const editPermission = useMemo(() => {
    if (isTemplateMode) {
      return isPlanActive ? Permissions.MODIFY_ACTIVE_PLAN_TEMPLATE : Permissions.PLAN_TEMPLATE_SETUP;
    }
    return Permissions.EDIT_DRAFT_PLAN;
  }, [isTemplateMode, isPlanActive]);

  const { observingOrganization } = useCurrentOrganization();
  const isTerminationStatus = useMemo(
    () =>
      observingOrganization.status === EmployerStatus.PendingTermination ||
      observingOrganization.status === EmployerStatus.TerminationInProgress ||
      observingOrganization.status === EmployerStatus.Terminated,
    [observingOrganization]
  );

  if (previewMode) {
    return null;
  }

  return (
    <Access
      accessRequirements={[
        {
          permission: editPermission,
        },
      ]}
    >
      <Box direction="column" align="center" margin={{ top: 'spacing24', bottom: 'spacing8' }}>
        <Text size="medium" color="textSecondary">
          To apply any changes, please switch to{' '}
          <Hint
            hintWidth={220}
            hideHint={
              (!isTerminationStatus && !isPlanTerminated && !isTemplateUnderUpdate) ||
              (isTemplateMode && !isTemplateUnderUpdate)
            }
            hintAlign="vertical"
            style={{ display: 'inline-flex' }}
            hintElement={
              <Anchor
                onClick={() => setEditMode(true)}
                weight="normal"
                label="Edit Mode"
                disabled={
                  isTerminationStatus || (isPlanTerminated && !isTemplateMode) || isTemplateUnderUpdate
                }
              />
            }
          >
            <Text size="small" color="textOnColor" textAlign="center">
              {isTemplateUnderUpdate
                ? TEMPLATE_UNDER_UPDATE_HINT
                : isPlanTerminated
                  ? 'Terminated plans cannot be edited.'
                  : 'Terminated organizations cannot edit plan.'}
            </Text>
          </Hint>
          .
        </Text>
      </Box>
    </Access>
  );
};

export default SwitchToEditMode;
